import { useEffect, useState } from "react";
import { api, formatError } from "../lib/api";
import { PageHeader, Modal } from "../components/ui-kit";
import { Plus, Trash2, KeyRound, Link2, Copy, Pencil } from "lucide-react";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", username: "", password: "", role: "tecnico", operator_ids: [] });
  const [err, setErr] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ id: "", name: "", email: "", username: "", role: "tecnico", new_password: "", operator_ids: [] });
  const [editErr, setEditErr] = useState("");

  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ user: null, new_password: "", confirm: "" });
  const [pwErr, setPwErr] = useState("");
  const [pwMsg, setPwMsg] = useState("");

  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUser, setLinkUser] = useState(null);
  const [linkData, setLinkData] = useState(null);
  const [linkErr, setLinkErr] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
    api.get("/operators").then((r) => setOperators(r.data)).catch(() => setOperators([]));
  }, []);

  const save = async () => {
    setErr("");
    if ((form.password || "").length < 8) { setErr("Senha inicial deve ter pelo menos 8 caracteres"); return; }
    try {
      const payload = {
        ...form,
        email: form.email.toLowerCase().trim(),
        username: form.username ? form.username.toLowerCase().trim() : undefined,
        operator_ids: form.operator_ids || [],
      };
      if (!payload.username) delete payload.username;
      await api.post("/users", payload);
      setOpen(false);
      setForm({ name: "", email: "", username: "", password: "", role: "tecnico", operator_ids: [] });
      load();
    } catch (e) { setErr(formatError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este usuário?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const openEdit = (u) => {
    setEditErr("");
    setEditForm({
      id: u.id,
      name: u.name || "",
      email: u.email || "",
      username: u.username || "",
      role: u.role || "tecnico",
      new_password: "",
      operator_ids: u.operator_ids || [],
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setEditErr("");
    if (editForm.new_password && editForm.new_password.length < 8) {
      setEditErr("Se preenchida, a nova senha deve ter pelo menos 8 caracteres");
      return;
    }
    const payload = {
      name: editForm.name.trim(),
      email: editForm.email.toLowerCase().trim(),
      username: editForm.username.trim().toLowerCase(),
      role: editForm.role,
      operator_ids: editForm.operator_ids || [],
    };
    if (editForm.new_password) payload.new_password = editForm.new_password;
    try {
      await api.put(`/users/${editForm.id}`, payload);
      setEditOpen(false);
      load();
    } catch (e) { setEditErr(formatError(e)); }
  };

  const toggleOperator = (list, id) => list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const openReset = (u) => {
    setPwForm({ user: u, new_password: "", confirm: "" });
    setPwErr(""); setPwMsg(""); setPwOpen(true);
  };
  const doReset = async () => {
    setPwErr(""); setPwMsg("");
    if (pwForm.new_password !== pwForm.confirm) { setPwErr("As senhas não coincidem"); return; }
    if (pwForm.new_password.length < 8) { setPwErr("Senha precisa ter pelo menos 8 caracteres"); return; }
    try {
      await api.put(`/users/${pwForm.user.id}/password`, { new_password: pwForm.new_password });
      setPwMsg(`Senha de ${pwForm.user.name} redefinida com sucesso.`);
      setTimeout(() => setPwOpen(false), 1500);
    } catch (e) { setPwErr(formatError(e)); }
  };

  const openLink = async (u) => {
    setLinkUser(u); setLinkData(null); setLinkErr(""); setLinkCopied(false); setLinkOpen(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-link`);
      setLinkData(data);
    } catch (e) { setLinkErr(formatError(e)); }
  };
  const copyLink = async () => {
    if (!linkData?.reset_url) return;
    await navigator.clipboard.writeText(linkData.reset_url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        eyebrow="Equipe"
        title="Usuários"
        actions={<button className="btn btn-primary" onClick={() => { setErr(""); setOpen(true); }} data-testid="new-user-btn"><Plus size={14} /> Novo Usuário</button>}
      />

      <div className="card">
        <table className="data">
          <thead><tr><th>Nome</th><th>Email</th><th>Usuário</th><th>Perfil</th><th>Ações</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td>{u.name}</td>
                <td className="mono">{u.email}</td>
                <td className="mono">{u.username || "-"}</td>
                <td><span className={`badge badge-role-${u.role}`}>{u.role}</span></td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    <button className="btn btn-ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`} title="Editar usuário">
                      <Pencil size={14} /> Editar
                    </button>
                    <button className="btn btn-ghost" onClick={() => openReset(u)} data-testid={`reset-pw-${u.email}`} title="Redefinir senha diretamente">
                      <KeyRound size={14} /> Senha
                    </button>
                    <button className="btn btn-ghost" onClick={() => openLink(u)} data-testid={`gen-link-${u.email}`} title="Gerar link para o usuário criar a própria senha">
                      <Link2 size={14} /> Link
                    </button>
                    <button className="btn btn-ghost" onClick={() => del(u.id)} title="Excluir usuário" data-testid={`del-user-${u.email}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Novo Usuário" testid="user-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="save-user-btn">Salvar</button>
        </>}>
        {err && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{err}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-caps block mb-1">Nome</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
          <div><label className="label-caps block mb-1">Email</label><input className="input mono" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
          <div><label className="label-caps block mb-1">Nome de Usuário (opcional)</label><input className="input mono" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="ex: joao" data-testid="user-username" /></div>
          <div><label className="label-caps block mb-1">Senha inicial (mín. 8 caract.)</label><input className="input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
          <div className="col-span-2"><label className="label-caps block mb-1">Perfil</label>
            <select className="select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
              <option value="tecnico">Técnico</option>
              <option value="almacenero">Almacenero</option>
              <option value="gestor">Gestor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-1">Operadoras que este usuário pode acessar</label>
            {operators.length === 0 ? (
              <div className="text-xs italic p-2 border" style={{ color: "var(--text-mute)", borderColor: "var(--border)" }}>
                Nenhuma operadora cadastrada. <a href="/operadoras" style={{ color: "var(--primary)" }}>Criar agora</a>.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="user-operators-picker">
                {operators.map((op) => {
                  const selected = form.operator_ids.includes(op.id);
                  return (
                    <button
                      type="button"
                      key={op.id}
                      onClick={() => setForm({ ...form, operator_ids: toggleOperator(form.operator_ids, op.id) })}
                      className="px-3 py-1 text-xs border flex items-center gap-2"
                      style={{
                        borderColor: selected ? (op.color || "var(--primary)") : "var(--border-strong)",
                        background: selected ? "rgba(56,189,248,0.08)" : "transparent",
                        color: selected ? "var(--text)" : "var(--text-dim)",
                      }}
                      data-testid={`user-op-toggle-${op.id}`}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: op.color || "var(--primary)" }} />
                      {op.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="text-xs mt-3" style={{ color: "var(--text-mute)" }}>
          O usuário poderá entrar com email ou com nome de usuário. Ambos são aceitos. Se atribuir mais de uma operadora, ele poderá alternar pelo menu superior.
        </div>
      </Modal>

      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar Usuário" testid="edit-user-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setEditOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={saveEdit} data-testid="save-edit-user">Salvar Alterações</button>
        </>}>
        {editErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{editErr}</div>}
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label-caps block mb-1">Nome</label><input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" /></div>
          <div><label className="label-caps block mb-1">Email</label><input className="input mono" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-user-email" /></div>
          <div><label className="label-caps block mb-1">Nome de Usuário</label><input className="input mono" value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} placeholder="opcional" data-testid="edit-user-username" /></div>
          <div>
            <label className="label-caps block mb-1">Perfil</label>
            <select className="select" value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })} data-testid="edit-user-role">
              <option value="tecnico">Técnico</option>
              <option value="almacenero">Almacenero</option>
              <option value="gestor">Gestor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-1">Nova senha (opcional, mín. 8 caract.)</label>
            <input className="input" type="password" value={editForm.new_password} onChange={(e) => setEditForm({ ...editForm, new_password: e.target.value })} placeholder="Deixe em branco para manter a senha atual" data-testid="edit-user-password" />
          </div>
          <div className="col-span-2">
            <label className="label-caps block mb-1">Operadoras que este usuário pode acessar</label>
            {operators.length === 0 ? (
              <div className="text-xs italic p-2 border" style={{ color: "var(--text-mute)", borderColor: "var(--border)" }}>
                Nenhuma operadora cadastrada. <a href="/operadoras" style={{ color: "var(--primary)" }}>Criar agora</a>.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="edit-user-operators-picker">
                {operators.map((op) => {
                  const selected = editForm.operator_ids.includes(op.id);
                  return (
                    <button
                      type="button"
                      key={op.id}
                      onClick={() => setEditForm({ ...editForm, operator_ids: toggleOperator(editForm.operator_ids, op.id) })}
                      className="px-3 py-1 text-xs border flex items-center gap-2"
                      style={{
                        borderColor: selected ? (op.color || "var(--primary)") : "var(--border-strong)",
                        background: selected ? "rgba(56,189,248,0.08)" : "transparent",
                        color: selected ? "var(--text)" : "var(--text-dim)",
                      }}
                      data-testid={`edit-user-op-toggle-${op.id}`}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: 999, background: op.color || "var(--primary)" }} />
                      {op.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="text-xs mt-3" style={{ color: "var(--text-mute)" }}>
          Para gerar um link e deixar o usuário definir a própria senha, use o botão "Link" na lista de usuários.
        </div>
      </Modal>

      <Modal open={pwOpen} onClose={() => setPwOpen(false)} title="Redefinir Senha (Admin)" testid="reset-password-modal"
        footer={<>
          <button className="btn btn-secondary" onClick={() => setPwOpen(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={doReset} data-testid="confirm-reset-pw">Redefinir</button>
        </>}>
        {pwErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{pwErr}</div>}
        {pwMsg && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(34,197,94,0.08)", borderColor: "rgba(34,197,94,0.3)", color: "#86EFAC" }}>{pwMsg}</div>}
        {pwForm.user && (
          <>
            <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
              <div className="label-caps mb-1">Usuário</div>
              <div className="text-sm">{pwForm.user.name}</div>
              <div className="text-xs mono mt-1" style={{ color: "var(--text-mute)" }}>{pwForm.user.email}{pwForm.user.username ? ` · ${pwForm.user.username}` : ""}</div>
            </div>
            <label className="label-caps block mb-1">Nova senha (mín. 8 caract.)</label>
            <input className="input mb-3" type="password" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="admin-new-pw" />
            <label className="label-caps block mb-1">Confirmar</label>
            <input className="input" type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="admin-confirm-pw" />
            <div className="text-xs mt-3" style={{ color: "var(--text-mute)" }}>Compartilhe a nova senha com o usuário por um canal seguro.</div>
          </>
        )}
      </Modal>

      <Modal open={linkOpen} onClose={() => setLinkOpen(false)} title="Gerar link de recuperação" testid="reset-link-modal"
        footer={<button className="btn btn-secondary" onClick={() => setLinkOpen(false)}>Fechar</button>}>
        {linkErr && <div className="mb-3 p-2 text-sm border" style={{ background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: "#FCA5A5" }}>{linkErr}</div>}
        {linkUser && (
          <div className="mb-3 p-3 border" style={{ borderColor: "var(--border-strong)" }}>
            <div className="label-caps mb-1">Usuário</div>
            <div className="text-sm">{linkUser.name}</div>
            <div className="text-xs mono mt-1" style={{ color: "var(--text-mute)" }}>{linkUser.email}</div>
          </div>
        )}
        {!linkData && !linkErr && (
          <div className="text-sm" style={{ color: "var(--text-dim)" }}>Gerando link…</div>
        )}
        {linkData && (
          <>
            <div className="label-caps mb-1">Link (válido por 2 horas)</div>
            <div className="p-2 mono text-xs mb-3 break-all" style={{ background: "#050B14", color: "var(--primary)" }} data-testid="reset-url-box">
              {linkData.reset_url}
            </div>
            <button className="btn btn-primary" onClick={copyLink} data-testid="copy-reset-link">
              <Copy size={13} /> {linkCopied ? "Copiado!" : "Copiar link"}
            </button>
            <div className="text-xs mt-3" style={{ color: "var(--text-mute)" }}>
              Envie o link ao usuário por um canal seguro. Cada solicitação gera um novo link.
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
